// Copyright 2020-2023 the .NET Foundation
// Licensed under the MIT License
// Toplevel documentation found at @/docs/engine/research-app-messages-index.md
import * as classicPywwt from './classic_pywwt';
import * as finderScope from './finder_scope';
import * as layers from './layers';
import * as selections from './selections';
import * as settings from './settings';
import * as tours from './tours';
/** Messages created for older versions of the
 * [pywwt](https://pywwt.readthedocs.io/) Python package.
 *
 * These can be grouped into the following categories:
 *
 * ### View Control
 *
 * - {@link CenterOnCoordinatesMessage}
 * - {@link PauseTimeMessage}
 * - {@link ResumeTimeMessage}
 * - {@link SetDatetimeMessage}
 * - {@link SetViewerModeMessage}
 * - {@link TrackObjectMessage}
 *
 * ### Image set Layers
 *
 * - {@link CreateImageSetLayerMessage}
 * - {@link ModifyFitsLayerMessage}
 * - {@link RemoveImageSetLayerMessage}
 * - {@link SetFitsLayerColormapMessage}
 * - {@link StretchFitsLayerMessage}
 *
 * ### Data Table Layers
 *
 * - {@link CreateTableLayerMessage}
 * - {@link ModifyTableLayerMessage}
 * - {@link RemoveTableLayerMessage}
 * - {@link UpdateTableLayerMessage}
 *
 * ### Image Sets
 *
 * - {@link LoadImageCollectionMessage}
 * - {@link LoadImageCollectionCompletedMessage}
 * - {@link SetBackgroundByNameMessage}
 * - {@link SetForegroundByNameMessage}
 * - {@link SetForegroundOpacityMessage}
 *
 * ### Annotations
 *
 * - {@link AddLinePointMessage}
 * - {@link AddPolygonPointMessage}
 * - {@link ClearAnnotationsMessage}
 * - {@link CreateAnnotationMessage}
 * - {@link ModifyAnnotationMessage}
 * - {@link RemoveAnnotationMessage}
 * - {@link SetCircleCenterMessage}
 *
 * ## Tours
 *
 * - {@link LoadTourMessage}
 * - {@link PauseTourMessage}
 * - {@link ResumeTourMessage}
 *
 * ## Miscellaneous
 *
 * - {@link ModifySettingMessage}
 */
export { classicPywwt };
export { finderScope };
/** Messages relating to layers.
 *
 * This module contains messages and types relating to various graphical layers
 * that can be added to the WWT view. However, note that the {@link classicPywwt}
 * module defines a bunch of messages on the same topic, especially concerning
 * imageset and data-table layers.
 *
 * The defined messages are:
 *
 * - {@link LoadHipsCatalogMessage} leading to {@link LoadHipsCatalogCompletedMessage}
 * - {@link GetHipsCatalogDataInViewMessage} leading to {@link GetHipsCatalogDataInViewReply}
 *
 **/
export { layers };
/** Messages related to selections.
 *
 * This module contains messages and types relating to interactive selection of
 * different items in the WWT display
 *
 * The defined messages are:
 *
 * - {@link AddSourceMessage}
 * - {@link ModifySelectabilityMessage}
 * - {@link ModifyAllSelectabilityMessage}
 * - {@link SelectionStateMessage}
 */
export { selections };
/** Various settings for the research app itself.
 *
 * This module contains messages and types relating to generic "settings" of the
 * research app that can be controlled. The {@link classicPywwt} module defines many
 * other settings and messages that control the appearance of graphical elements
 * in the WWT engine such as layers and annotations.
 *
 * The defined messages are:
 *
 * - {@link ModifySettingsMessage}
 * */
export { settings };
/** Messages relating to tours.
 *
 * This module contains messages and types relating to WWT tours, in particular
 * obtaining the current view as a tour. However, note that the
 * {@link classicPywwt} module defines a bunch of messages on the same topic,
 * especially concerning loading and playback of tours.
 *
 * The defined messages are:
 *
 * - {@link GetViewAsTourMessage} leading to {@link GetViewAsTourReply}
 */
export { tours };
/** Type guard function for ViewStateMessage. */
export function isViewStateMessage(o) {
    return typeof o.type === "string" &&
        o.type == "wwt_view_state" &&
        typeof o.sessionId === "string" &&
        typeof o.raRad === "number" &&
        typeof o.decRad === "number" &&
        typeof o.fovDeg === "number" &&
        typeof o.engineClockISOT === "string" &&
        typeof o.systemClockISOT === "string" &&
        typeof o.engineClockRateFactor === "number";
}
/** Type guard function for {@link PingPongMessage}. */
export function isPingPongMessage(o) {
    return typeof o.type === "string" &&
        o.type == "wwt_ping_pong" &&
        typeof o.threadId === "string" &&
        (o.sessionId === undefined || typeof o.sessionId === "string");
}
/** Type guard function for {@link PointerMoveMessage}. */
export function isPointerMoveMessage(o) {
    return typeof o.type === "string" &&
        o.type == "wwt_pointer_move" &&
        typeof o.clientX === "number" &&
        typeof o.clientY === "number" &&
        (o.sessionId === undefined || typeof o.sessionId === "string");
}
/** Type guard function for {@link PointerUpMessage}. */
export function isPointerUpMessage(o) {
    return typeof o.type === "string" &&
        o.type == "wwt_pointer_up" &&
        typeof o.clientX === "number" &&
        typeof o.clientY === "number" &&
        (o.sessionId === undefined || typeof o.sessionId === "string");
}
/** Type guard function for {@link ClearTileCacheMessage} */
export function isClearTileCacheMessage(obj) {
    return typeof obj.event === "string" && obj.event === "clear_tile_cache";
}
//# sourceMappingURL=index.js.map